import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import RegularFormSettings from './RegularFormSettings';
import TriggerFormSettings from './TriggerFormSettings';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
    useGetExternalUserFormAssignmentsQuery,
    useGetFormQuestionAssignmentQuery
} from '../../../../api/services/formAssignmentsService';
import {useLazyGetFormQuery} from '../../../../api/services/formsService';
import Tooltip from '@mui/material/Tooltip';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import {FORM_ACCESS_LEVELS} from '../../../../constants/formAccessLevels';
import FormHelperText from '@mui/material/FormHelperText';
import {validateAccessLevel, validateFieldLength} from '../../../../utility/validationUtil';
import EmailsInput from '../../../../shared-components/EmailsInput';
import SettingsSectionBase from './SettingsSection';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CancelIcon from '@mui/icons-material/Close';
import AutoAwesomeMotionOutlinedIcon from '@mui/icons-material/AutoAwesomeMotionOutlined';
import {DatePicker} from '@mui/x-date-pickers';
import moment from 'moment/moment';
import useTextField from '../../../../utility/hooks/useTextField';
import CircleIcon from '@mui/icons-material/Circle';
import IconButton from '@mui/material/IconButton';

const PrivateFormSettings = (
    {
        formId,
        name,
        onNameChange,
        expirationDate,
        onExpirationDateChange,
        remindInterval,
        onRemindIntervalChange,
        categories,
        onCategoriesChange,
        formCategories,
        isFormLive,
        isFormArchived,
        strictRecipientsFiltering,
        allParentsCanSubmit,
        allowMultipleSubmissions,
        audience,
        audienceOptions,
        settings,
        accessLevel,
        disableAccessLevel,
        notificationEmails,
        onSettingsChange,
        onSetStrictRecipientsFiltering,
        onAudienceChange,
        onAllParentsCanSubmitChange,
        onAllowMultipleSubmissionsChange,
        onAccessLevelChange,
        onNotificationEmailsChange,
        enableRecipientNotifications,
        onEnableRecipientNotificationsChange,
        isCommunicationForm,
        onCommunicationTypeChange,
        canSwitchToEmail
    }) => {
    const [enableTrigger, setEnableTrigger] = useState(false);
    const {
        data: formQuestionAssigment
    } = useGetFormQuestionAssignmentQuery(formId);
    const {data: externalUserFormAssignments} = useGetExternalUserFormAssignmentsQuery(
        {formId},
        {
            skip: audience !== FORM_AUDIENCE_TYPE.externalUsers
        });
    const [getForm] = useLazyGetFormQuery();

    const isCommunicationMode = isCommunicationForm === true;

    useEffect(() => {
        if (formQuestionAssigment?.id) {
            getForm(formQuestionAssigment.relatedFormId)
                .then(({data: form}) => {
                    const triggerSettings = {
                        id: formQuestionAssigment.id,
                        selectedForm: {
                            id: formQuestionAssigment.relatedFormId,
                            name: form.name,
                            schema: form.schema
                        },
                        relatedComponent: form.schema.find(e => e.id === formQuestionAssigment.relatedComponentId),
                        targetState: formQuestionAssigment.targetState,
                    };

                    onSettingsChange(prev => ({
                        ...prev,
                        triggerFormSettings: {
                            ...triggerSettings,
                            original: triggerSettings
                        }
                    }));
                    setEnableTrigger(true);
                });
        }
    }, [formQuestionAssigment]);

    useEffect(() => {
        if (isCommunicationMode && enableTrigger) {
            setEnableTrigger(false);
        }
    }, [isCommunicationMode, enableTrigger]);

    const handleAudienceChange = (event) => {
        const newAudienceType = event.target.value;

        if (newAudienceType === FORM_AUDIENCE_TYPE.students || newAudienceType === FORM_AUDIENCE_TYPE.employees) {
            setEnableTrigger(false);
        }

        onAudienceChange(newAudienceType);
    };

    const handleAccessLevelChange = (event, value) => {
        const level = parseInt(value);

        onAccessLevelChange(level);
        onSettingsChange(prev => ({
            ...prev,
            validation: {
                ...prev.validation,
                ...validateAccessLevel(level, prev.groupFilters)
            }
        }));
    };

    const nameField = useTextField({
        initialValue: name ?? '',
        validate: (value) => validateFieldLength(value, 128),
        onChange: (event) => onNameChange(event.target.value)
    });

    return (
        <Box p={2} pt={2}>
            <Box
                display="flex"
                flexDirection="column"
                sx={{
                    width: {
                        xs: '100%',
                        sm: '65%',
                        md: '50%',
                        lg: '45%',
                    },
                    mx: 'auto',
                    gap: 2,
                    mt: 2,
                }}
            >
                <SettingsSectionBase
                    header="Form Details"
                    headerIcon={<DescriptionOutlinedIcon fontSize="small"/>}
                >
                    <Stack spacing={3}>
                        <TextField
                            fullWidth
                            label="Form Name"
                            required={nameField.required}
                            disabled={isFormArchived}
                            value={nameField.value}
                            onChange={nameField.onChange}
                            error={nameField.error}
                            helperText={nameField.helperText || ' '}
                            autoComplete="off"
                        />
                        {(() => {
                            const selectedCategories = categories || [];

                            const handleCategoryChange = (e) => {
                                onCategoriesChange(e.target.value);
                            };

                            return (
                                <TextField
                                    select
                                    fullWidth
                                    disabled={isFormArchived}
                                    value={selectedCategories}
                                    label="Categories"
                                    helperText=" "
                                    SelectProps={{
                                        multiple: true,
                                        renderValue: (selected) => {
                                            if (!Array.isArray(selected) || selected.length === 0) {
                                                return <em>No categories</em>;
                                            }

                                            const selectedCats = formCategories?.filter(c =>
                                                selected.includes(c.id)
                                            ) || [];

                                            return (
                                                <Stack flexDirection="row" gap={1} flexWrap="wrap">
                                                    {selectedCats.map(cat => (
                                                        <Box
                                                            key={cat.id}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.8,
                                                                padding: 1,
                                                                borderRadius: '18px',
                                                                border: '1px solid #adadad',
                                                            }}
                                                        >
                                                            <CircleIcon
                                                                fontSize="small"
                                                                sx={{color: cat.color}}
                                                            />
                                                            <Typography variant="body2">
                                                                {cat.name}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            );
                                        },
                                        MenuProps: {
                                            sx: {
                                                '& .MuiMenuItem-root': {
                                                    whiteSpace: 'unset',
                                                    wordBreak: 'break-all'
                                                }
                                            }
                                        }
                                    }}
                                    onChange={handleCategoryChange}
                                >
                                    {formCategories?.map(c => (
                                        <MenuItem key={c.id} value={c.id}>
                                            <Checkbox checked={selectedCategories.includes(c.id)}/>

                                            <CircleIcon
                                                fontSize="small"
                                                sx={{
                                                    color: c.color,
                                                    mr: 1
                                                }}
                                            />

                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            );
                        })()}
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={{flexGrow: 1}}>
                                <DatePicker
                                    label="Expiration Date"
                                    disabled={isFormArchived || isCommunicationMode}
                                    value={expirationDate ? moment(expirationDate) : null}
                                    onChange={(v) => {
                                        if (!v) return onExpirationDateChange(null);

                                        const endOfDayUtc = v
                                            .clone()
                                            .utc()
                                            .endOf('day');
                                        onExpirationDateChange(endOfDayUtc.toISOString());
                                    }}
                                    timezone="UTC"
                                    format="MMM D, YYYY"
                                    minDate={
                                        settings.scheduledActivationAt
                                            ? moment(settings.scheduledActivationAt)
                                            : moment()
                                    }
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            helperText: 'Expires at 23:59 (UTC) on this date',
                                            inputProps: {readOnly: true},
                                        },
                                        field: {readOnly: true},
                                    }}
                                />

                            </Box>

                            {expirationDate && !isFormArchived && !isCommunicationMode && (
                                <IconButton
                                    aria-label="Clear expiration date"
                                    onClick={() => onExpirationDateChange(null)}
                                    size="small"
                                    sx={{
                                        transform: 'translateY(-10px)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <CancelIcon fontSize="small"/>
                                </IconButton>
                            )}
                        </Stack>

                        <TextField
                            fullWidth
                            required
                            label="Remind interval"
                            variant="outlined"
                            disabled={isFormArchived || isCommunicationMode}
                            value={remindInterval}
                            onChange={(e) => onRemindIntervalChange(e.target.value.replace(/\D/g, ''))}
                            helperText="In hours (0 = Disabled)"
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*'
                            }}
                        />

                    </Stack>
                </SettingsSectionBase>
                <Divider sx={{my: 2}}/>
                <SettingsSectionBase
                    header={audience === FORM_AUDIENCE_TYPE.none ? 'Select audience' : 'Audience'}
                    headerIcon={<GroupsOutlinedIcon fontSize="small"/>}
                    subHeader={
                        audience === FORM_AUDIENCE_TYPE.none
                            ? 'Choose who will receive and complete this form'
                            : null
                    }
                >
                    <TextField
                        select
                        fullWidth
                        label="Audience"
                        value={audience}
                        onChange={handleAudienceChange}
                        disabled={
                            settings && ([
                                    ...settings.groupFilters,
                                    ...settings?.studentFilters,
                                    ...settings?.applicationFilters,
                                    ...settings?.enquiryFilters,
                                    ...settings?.externalUserEmailAssignments,
                                    ...settings?.employeeFilters,
                                ].filter(x => x.isDeleted == null || !x.isDeleted).length > 0 ||
                                settings.triggerFormSettings.selectedForm != null ||
                                allowMultipleSubmissions)
                        }
                    >
                        <MenuItem disabled value={FORM_AUDIENCE_TYPE.none}>None</MenuItem>
                        {
                            audienceOptions
                                .map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))
                        }
                    </TextField>
                </SettingsSectionBase>
                {
                    audience === FORM_AUDIENCE_TYPE.none ||
                    allowMultipleSubmissions ||
                    isCommunicationMode
                        ? null
                        : <>
                            <Divider sx={{
                                mt: 4,
                                mb: 3
                            }}/>
                            <SettingsSectionBase
                                header={'Trigger Form'}
                                headerIcon={<AutoAwesomeMotionOutlinedIcon fontSize="small"/>}
                                subHeader={
                                    audience === FORM_AUDIENCE_TYPE.none
                                        ? 'Choose who will receive and complete this form'
                                        : null
                                }
                            >
                                <FormControl variant="standard">
                                    <FormLabel component="legend">When enabled, this form will be triggered automatically
                                        based on the conditions set.</FormLabel>
                                    <FormGroup>
                                        <Stack flexDirection="row" alignItems="center">
                                            <FormControlLabel
                                                control={<Switch
                                                    color="primary"
                                                    checked={enableTrigger}
                                                    onChange={e => {
                                                        const checked = e.target.checked;
                                                        setEnableTrigger(checked);
                                                        if (checked) {
                                                            onSettingsChange(prev => ({
                                                                ...prev,
                                                                groupFilters: [],
                                                                studentFilters: [],
                                                                employeeFilters: [],
                                                                applicationFilters: [],
                                                                enquiryFilters: [],
                                                                externalUserEmailAssignments: [],
                                                            }));
                                                        }
                                                    }}
                                                    disabled={settings.triggerFormSettings.selectedForm != null ||
                                                        audience === FORM_AUDIENCE_TYPE.students ||
                                                        audience === FORM_AUDIENCE_TYPE.none || isFormArchived || isFormLive}
                                                />}
                                                label={enableTrigger ? 'Disable Trigger' : 'Enable Trigger'}
                                            />
                                            {
                                                audience === FORM_AUDIENCE_TYPE.students
                                                    ? <Tooltip
                                                        title="Trigger forms are not currently available for this audience type">
                                                        <InfoOutlinedIcon fontSize="small"/>
                                                    </Tooltip>
                                                    : null

                                            }
                                        </Stack>
                                    </FormGroup>
                                </FormControl>
                            </SettingsSectionBase>
                        </>
                }
                {audience === FORM_AUDIENCE_TYPE.none
                    ? null
                    : (<>
                        <Divider sx={{my: 3}}/>
                        <SettingsSectionBase
                            header={'Settings'}
                            headerIcon={<SettingsOutlinedIcon fontSize="small"/>}
                            subHeader={
                                audience === FORM_AUDIENCE_TYPE.none
                                    ? 'Choose who will receive and complete this form'
                                    : null
                            }
                        >
                            <EmailsInput
                                label="Email address to be notified when this form receives submissions"
                                values={notificationEmails}
                                onChange={emails =>
                                    onNotificationEmailsChange(
                                        Array.from(new Map(emails.map(item => [item.email, item])).values())
                                    )
                                }
                                onBlur={email =>
                                    onNotificationEmailsChange(prev =>
                                        Array.from(new Map([
                                            ...prev.map(item => [item.email, item]),
                                            [email, {email}],
                                        ]).values())
                                    )
                                }
                                disabled={isFormArchived || isCommunicationMode}
                            />
                        </SettingsSectionBase>

                    </>)
                }
                {
                    audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
                    audience === FORM_AUDIENCE_TYPE.employees
                        ? <>
                            <FormControlLabel
                                sx={{gap: 1}}
                                control={
                                    <Checkbox
                                        sx={{
                                            padding: 0,
                                            marginLeft: 1
                                        }}
                                        checked={allowMultipleSubmissions}
                                        onChange={async (e) =>
                                            await onAllowMultipleSubmissionsChange(e.target.checked)
                                        }
                                        disabled={isFormLive || enableTrigger || isFormArchived || isCommunicationMode}
                                    />
                                }
                                label={
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                    }}>
                                        Self-service form
                                        <Tooltip
                                            title="A reusable form that stays available on the user’s profile and can be completed multiple times while it remains active."
                                            placement="right"
                                        >
                                            <InfoOutlinedIcon fontSize="small"
                                                              sx={{
                                                                  color: 'text.secondary',
                                                                  cursor: 'pointer'
                                                              }}/>
                                        </Tooltip>
                                    </Box>
                                }
                            />
                        </>
                        : null
                }
                {
                    audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion && !allowMultipleSubmissions
                        ? (<FormControlLabel
                                sx={{
                                    gap: 1,
                                }}
                                control={
                                    <Checkbox
                                        sx={{
                                            padding: 0,
                                            marginLeft: 1
                                        }}
                                        checked={allParentsCanSubmit}
                                        onChange={async (e) =>
                                            await onAllParentsCanSubmitChange(e.target.checked)
                                        }
                                        disabled={isFormLive || isFormArchived || isCommunicationMode}
                                    />
                                }
                                label={
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                    }}>
                                        Require both parents to complete the form
                                        <Tooltip
                                            title="When checked, all eligible contacts receive a form to complete individually for the linked profile. When unchecked, one form is sent to all, and the first to submit completes it."
                                            placement="right"
                                        >
                                            <InfoOutlinedIcon
                                                fontSize="small"
                                                sx={{
                                                    color: 'text.secondary',
                                                    cursor: 'pointer'
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                }
                            />
                        )
                        : null
                }
                {
                    audience === FORM_AUDIENCE_TYPE.none ||
                    allowMultipleSubmissions
                        ? null
                        : <FormControlLabel
                            sx={{
                                gap: 1,
                            }}
                            control={
                                <Checkbox
                                    sx={{
                                        padding: 0,
                                        marginLeft: 1
                                    }}
                                    disabled={isFormArchived || isCommunicationMode}
                                    checked={enableRecipientNotifications}
                                    onChange={e => onEnableRecipientNotificationsChange(e.target.checked)}
                                />
                            }
                            label={
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                }}>
                                    Enable recipient notifications
                                    <Tooltip
                                        title="When unchecked, the recipient will not receive an email notification for this form when it becomes active."
                                        placement="right"
                                    >
                                        <InfoOutlinedIcon
                                            fontSize="small"
                                            sx={{
                                                color: 'text.secondary',
                                                cursor: 'pointer'
                                            }}
                                        />
                                    </Tooltip>
                                </Box>
                            }
                        />
                }
                {
                    audience === FORM_AUDIENCE_TYPE.none
                        ? null
                        : (
                            <FormControl disabled={disableAccessLevel || isCommunicationMode}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    my: 1
                                }}>
                                    <FormLabel>Access level</FormLabel>
                                    <Tooltip
                                        title="When set to Private, the user must have a registered account to sign in and access the form. When set to Public, the form can be completed without signing in."
                                        placement="right"
                                    >
                                        <InfoOutlinedIcon
                                            fontSize="small"
                                            sx={{
                                                color: 'text.secondary',
                                                cursor: 'pointer'
                                            }}
                                        />
                                    </Tooltip>
                                </Box>

                                <RadioGroup
                                    row
                                    value={accessLevel}
                                    onChange={handleAccessLevelChange}
                                >
                                    <FormControlLabel
                                        value={FORM_ACCESS_LEVELS.private}
                                        control={<Radio/>}
                                        label="Private"
                                        disabled={isFormLive || isFormArchived || isCommunicationMode}
                                    />
                                    <FormControlLabel
                                        value={FORM_ACCESS_LEVELS.publicWithStudentLink}
                                        control={<Radio/>}
                                        label="Public"
                                        disabled={isFormLive || isFormArchived || isCommunicationMode}
                                    />
                                </RadioGroup>

                                {settings.validation?.accessLevel?.warning
                                    ? (<FormHelperText sx={{marginLeft: 0}} error>
                                            {settings.validation?.accessLevel?.warning}
                                        </FormHelperText>
                                    )
                                    : null}
                            </FormControl>
                        )
                }
                <Box>
                    <FormControl
                        component="fieldset"
                        disabled={isFormLive || isFormArchived}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mb: 1,
                            }}
                        >
                            <FormLabel>Communication type</FormLabel>
                            <Tooltip
                                title="Choose whether this communication is sent as an email only or as a form for the user to complete."
                                placement="right"
                            >
                                <InfoOutlinedIcon
                                    fontSize="small"
                                    sx={{
                                        color: 'text.secondary',
                                        cursor: 'pointer',
                                    }}
                                />
                            </Tooltip>
                        </Box>

                        <RadioGroup
                            row
                            value={isCommunicationForm ? 'email' : 'form'}
                            onChange={(event) => {
                                const value = event.target.value;
                                const nextIsEmail = value === 'email';

                                if (!isCommunicationForm && nextIsEmail && !canSwitchToEmail) {
                                    return;
                                }

                                onCommunicationTypeChange?.(nextIsEmail);
                            }}
                        >
                            <FormControlLabel
                                value="email"
                                control={<Radio/>}
                                label="Email"
                                disabled={
                                    isFormLive ||
                                    isFormArchived ||
                                    (!isCommunicationForm && !canSwitchToEmail)
                                }
                            />
                            <FormControlLabel
                                value="form"
                                control={<Radio/>}
                                label="Form"
                                disabled={isFormLive || isFormArchived}
                            />
                        </RadioGroup>

                    </FormControl>
                </Box>
                {
                    audience === FORM_AUDIENCE_TYPE.none
                        ? <Box sx={{my: 3}}/>
                        : <Divider sx={{my: 3}}/>
                }
            </Box>

            {
                audience === FORM_AUDIENCE_TYPE.none
                    ? null
                    : <>
                        <Box
                            sx={{
                                py: 3,
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    width: '100%',
                                    maxWidth: {
                                        xs: '100%',
                                        sm: '65%',
                                        md: '50%',
                                        lg: '45%',
                                    },
                                }}
                            >
                                {
                                    !enableTrigger || isCommunicationMode
                                        ? <RegularFormSettings
                                            formId={formId}
                                            audience={audience}
                                            settings={settings}
                                            strictRecipientsFiltering={strictRecipientsFiltering}
                                            isFormLive={isFormLive}
                                            isFormArchived={isFormArchived}
                                            accessLevel={accessLevel}
                                            externalUsers={externalUserFormAssignments}
                                            onSettingsChange={onSettingsChange}
                                            onSetStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                                        />
                                        : <TriggerFormSettings
                                            formId={formId}
                                            formQuestionAssigment={formQuestionAssigment}
                                            settings={settings}
                                            audience={audience}
                                            externalUsers={externalUserFormAssignments}
                                            onSettingsChange={onSettingsChange}
                                        />
                                }
                            </Box>
                        </Box>
                    </>
            }
        </Box>
    );
};

PrivateFormSettings.defaultProps = {
    isFormLive: false,
};

PrivateFormSettings.propTypes = {
    formId: PropTypes.number,
    name: PropTypes.string,
    onNameChange: PropTypes.func,
    expirationDate: PropTypes.string,
    remindInterval: PropTypes.number,
    onRemindIntervalChange: PropTypes.func,
    onExpirationDateChange: PropTypes.func,
    categories: PropTypes.array,
    onCategoriesChange: PropTypes.func,
    formCategories: PropTypes.array,
    isFormLive: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    strictRecipientsFiltering: PropTypes.bool,
    audience: PropTypes.number,
    audienceOptions: PropTypes.array,
    settings: PropTypes.object,
    allParentsCanSubmit: PropTypes.bool,
    allowMultipleSubmissions: PropTypes.bool,
    accessLevel: PropTypes.number,
    disableAccessLevel: PropTypes.bool,
    notificationEmails: PropTypes.array,
    onSettingsChange: PropTypes.func,
    onGroupFiltersChange: PropTypes.func,
    onStudentFiltersChange: PropTypes.func,
    onSetStrictRecipientsFiltering: PropTypes.func,
    onAudienceChange: PropTypes.func,
    onAllParentsCanSubmitChange: PropTypes.func,
    onAllowMultipleSubmissionsChange: PropTypes.func,
    onAccessLevelChange: PropTypes.func,
    onNotificationEmailsChange: PropTypes.func,
    enableRecipientNotifications: PropTypes.bool,
    onEnableRecipientNotificationsChange: PropTypes.func,
    isCommunicationForm: PropTypes.bool,
    onCommunicationTypeChange: PropTypes.func,
    canSwitchToEmail: PropTypes.bool,
};

export default PrivateFormSettings;
